/* eslint-disable react/no-danger */
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { AutoSizer } from 'react-virtualized';
import { withRouter } from 'react-router-dom';
import Collection from 'core/model/Collection';
import { Box, Button, Card, Flex, Heading, Icon, Text } from 'core/components';
import { VirtualizedTable } from 'core/components/table';
import Map from 'app/components/map';
import AgentRenderer from 'app/views/synthetics/components/AgentRenderer';
import {
  getSubtestUrl,
  getWorstHealth,
  healthToIntent,
  healthToMarkerColor,
  healthToIcon
} from 'app/views/synthetics/utils/syntheticsUtils';
import { HEALTH_SORT } from '@kentik/ui-shared/synthetics/constants';
import { getWorstHealthInArray } from '@kentik/ui-shared/synthetics/utils';

@inject('$syn')
@observer
class TestResultsMap extends Component {
  static defaultProps = {
    height: 800
  };

  state = {
    modelId: null,
    title: null,
    selectedTime: null
  };

  static getDerivedStateFromProps(props, state) {
    const nextSelectedTime = props.test.results.resultTime;
    const { selectedTime } = state;

    // as we traverse the alarm timeline, the result time on the test results will change
    // when this happens, reset the selected marker state and update the selected time
    if (nextSelectedTime !== selectedTime) {
      return { modelId: null, title: null, selectedTime: nextSelectedTime };
    }
    return null;
  }

  handleDetailsClose = () => this.setState({ modelId: null, title: null });

  /*
    On click of a marker, show a panel that lists the source agent and targets
    sorted by the worst health status at the top
  */
  handleMarkerClick = ({ modelId, title }) => {
    const { agents } = this.markersObj[modelId];
    // flatten the data that represents source/target/health and sort the worst first
    const markerData = agents
      .reduce(
        (acc, agentData) =>
          acc.concat(
            agentData.targets.map((target) => ({
              agentId: agentData.id,
              agentHealth: agentData.health,
              target
            }))
          ),
        []
      )
      .sort((a, b) => {
        const aIndex = HEALTH_SORT.indexOf(a.target.health);
        const bIndex = HEALTH_SORT.indexOf(b.target.health);

        // ensure absent health is pinned to the bottom
        return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
      });

    // the marker details will display grouped by agent
    // this becomes particularly important when looking at a map that bundles agents that share the same coordinates
    const markerCollection = new Collection(markerData, { groupBy: 'agentId' });

    this.setState({ modelId, title, markerCollection });
  };

  // navigates to a source/target subtest results view
  handleRowClick = (model) => {
    const { history, test } = this.props;
    const { agentId, target } = model.get();
    const to = getSubtestUrl({
      agent_id: agentId,
      target: target.target_agent_id || target.target,
      test
    });

    history.push(to);
  };

  // builds a reference dataset representing source agents, targets, and health
  get agents() {
    const { $syn, test } = this.props;
    const { selectedTime } = this.state;
    const { agentTaskConfig, results, resultsKeys } = test.results;
    const { agentKeys } = resultsKeys;
    const agents = {};

    for (let i = 0; i < agentKeys.length; i += 1) {
      const id = agentKeys[i];
      const agent = results.agents[id];

      if (agent) {
        const { lat, long, agent_alias } = agent;
        const health = test.results.agentHealthTs[id] || {};
        const { geoCoords } = $syn.agents.get(id) || { geoCoords: { lat, lon: long } };
        const testTarget = test.get('config.target.value');

        agents[id] = {
          id,
          lat: geoCoords.lat,
          lon: geoCoords.lon,
          title: agent_alias,
          targets: agentTaskConfig?.[id]?.targets.reduce((acc, target) => {
            if (test.isHostname) {
              if (health.targets[testTarget]?.ping[selectedTime]?.dst_ip === target) {
                acc.push({
                  target,
                  health: health.targets[testTarget].overall[selectedTime].health
                });
              }
            } else if (test.isAgentTest || test.isMesh) {
              const target_agents = $syn.agents.getTargetAgentsByIps([target]) || [];
              const target_agent_id = target_agents[0]?.id || target;
              acc.push({
                target: target_agents[0]?.label || target,
                target_agent_id,
                health: health?.targets?.[target_agent_id]?.overall?.[selectedTime]?.health
              });
            } else if (test.isTransaction) {
              acc.push({
                target: 'Transaction',
                health: health?.targets?.transaction?.overall?.[selectedTime]?.health
              });
            } else {
              acc.push({
                target,
                health: health?.targets?.[target]?.overall?.[selectedTime]?.health
              });
            }

            return acc;
          }, []),
          health
        };
      }
    }

    return agents;
  }

  // returns a list of markers to populate the map
  // builds a reference of the same markers to be used for assembling details
  get markers() {
    const { test } = this.props;
    const { resultsKeys } = test.results;
    const { agentKeys } = resultsKeys;
    const { agents } = this;
    const markers = [];
    this.markersObj = {};

    // loop through all agents and create a map marker for each unique latLng coords
    // if there are more agents at those coords, bundle the agent, targets, and health into the same coordinate
    for (let i = 0; i < agentKeys.length; i += 1) {
      const id = agentKeys[i];
      const agent = agents[id];

      if (agent) {
        const { lat, lon, title, targets } = agent;
        const worstAgentHealth = getWorstHealthInArray(targets, 'health');
        const key = `${lat}|${lon}`;
        let marker = this.markersObj[key];

        if (!marker) {
          // initialize a marker for this geo location
          marker = {
            idx: markers.length,
            agents: [{ id, targets, health: worstAgentHealth }],
            health: worstAgentHealth
          };
          this.markersObj[key] = marker;
          markers.push({
            id: `${lat}|${lon}`,
            lat,
            lon,
            title,
            modelId: `${lat}|${lon}`,
            color: healthToMarkerColor({ health: worstAgentHealth, isAggregated: test.results.isAggregated }),
            element: 'dot'
          });
        } else {
          // we're bundling another agent into an established marker for this geo location
          // the health on the marker is the current worst health at this location so compare that with the current agent's worst health
          const worstHealth = getWorstHealth(worstAgentHealth, marker.health);
          marker.health = worstHealth;
          marker.agents.push({ id, targets, health: worstAgentHealth });
          markers[marker.idx].color = healthToMarkerColor({
            health: worstHealth,
            isAggregated: test.results.isAggregated
          });
        }
      }
    }

    return markers;
  }

  // grouping renderer for the side panel
  // displays overall health icon/intent for the agent as well as target count
  groupSummaryLookup = ({ groupKey, group }) => {
    const { $syn, test } = this.props;
    const model = group?.[0];
    const agent = $syn.agents.get(groupKey);
    const healthConfig = { health: model.get('agentHealth'), isAggregated: test.results.isAggregated };

    if (agent) {
      return (
        <Flex alignItems="center" gap={1}>
          <Icon icon={healthToIcon(healthConfig)} color={healthToIntent(healthConfig)} />
          <AgentRenderer agent={agent} showLocationInfo />
          <Text>{`${group.length} target${group.length === 1 ? '' : 's'}`}</Text>
        </Flex>
      );
    }

    return 'N/A';
  };

  get columns() {
    const { $syn, test } = this.props;

    return [
      {
        label: 'Status',
        id: 'status',
        width: 66,
        sortable: false,
        align: 'right',
        renderer: ({ model }) => {
          const target = model.get('target');
          const healthConfig = { health: target.health, isAggregated: test.results.isAggregated };

          return <Icon icon={healthToIcon(healthConfig)} color={healthToIntent(healthConfig)} />;
        }
      },

      {
        label: 'Target',
        name: 'target',
        sortable: false,
        renderer: ({ value }) => {
          const { target, target_agent_id } = value;

          if (target_agent_id) {
            const targetAgent = $syn.agents.get(target_agent_id);

            if (targetAgent) {
              return <AgentRenderer agent={targetAgent} showLocationInfo />;
            }
          }

          return target;
        }
      }
    ];
  }

  // content for the side panel details
  // display the title of the geographic location and a table that breaks down source/target/health status
  get markerDetails() {
    const { modelId, title, markerCollection } = this.state;

    if (!modelId) {
      return null;
    }

    return (
      <>
        <Flex justifyContent="space-between" position="sticky" top={0} bg="appBackground">
          <Heading level={5} p={1} m={0}>
            {title}
          </Heading>
          <Button icon="cross" onClick={this.handleDetailsClose} minimal small />
        </Flex>
        <VirtualizedTable
          key={modelId} // force a re-mount when we click markers on the map with the panel open to ensure we get reliable, collapsed table states
          collection={markerCollection}
          columns={this.columns}
          groupSummaryLookup={this.groupSummaryLookup}
          stickyHeaderOffset={35}
          selectOnRowClick={false}
          onRowClick={this.handleRowClick}
          isCollapsed
          stickyHeader
          stickyGroups
          flexed
        />
      </>
    );
  }

  render() {
    const { onLoad, height } = this.props;
    const { markers } = this;
    return (
      <Card display="flex" mb={2} overflow="hidden" minHeight={height}>
        <Box flex={1}>
          <AutoSizer disableHeight>
            {({ width }) => {
              if (width === 0) {
                return null;
              }
              return (
                <Map
                  containerWrapperProps={{ width, height, borderRadius: 4 }}
                  markerSelectOnClick
                  markerPopupOnHover
                  markers={markers}
                  submarkers={[]}
                  autoBounds
                  maxZoom={16}
                  onLoad={onLoad}
                  hideNonSelectedMarkers={false}
                  onMarkerClick={this.handleMarkerClick}
                />
              );
            }}
          </AutoSizer>
        </Box>
        <Flex
          flexDirection="column"
          width={this.markerDetails ? 400 : 0}
          height={height}
          borderLeft="thin"
          overflow="auto"
        >
          {this.markerDetails}
        </Flex>
      </Card>
    );
  }
}

export default withRouter(TestResultsMap);
